import { createBrowserRouter, Navigate } from 'react-router';
import { DevDemoTabs } from 'features/dev-demo/types/DemoTabs';
import { RouteLoadingReporter } from 'features/loading';
import { RoutePath } from 'features/routing/constants';
import { DemoProtectedRoutes } from 'features/routing/DemoProtectedRoutes';
import { NavigationProvider } from 'features/routing/NavigationProvider';
import { ProtectedAdminRoutes } from 'features/routing/ProtectedAdminRoutes';
import { ProtectedParticipationRoutes } from 'features/routing/ProtectedParticipationRoutes';
import { ProtectedRoutes } from 'features/routing/ProtectedRoutes';
import {
  loadHomePage,
  loadParticipationTaskPage,
} from './preloadParticipationPages';

const addDevRoutes = import.meta.env.PUBLIC_ALLOW_DEVELOPER_FEATURES === 'true';

export const router = createBrowserRouter([
  {
    element: <NavigationProvider />,
    hydrateFallbackElement: <RouteLoadingReporter />,
    children: [
      {
        path: RoutePath.AuthCallback,
        lazy: () =>
          import('../features/auth/components/AuthCallback').then(m => ({
            Component: m.AuthCallback,
          })),
      },
      {
        element: <ProtectedRoutes />,
        children: [
          {
            path: RoutePath.Root,
            element: <Navigate to={RoutePath.StudyChoice} replace />,
          },
          {
            path: RoutePath.ProtocolStart,
            lazy: () => import('./protocol-start/ProtocolStartPage'),
          },
          {
            path: RoutePath.StudyChoice,
            lazy: () => import('./study-choice/StudyChoicePage'),
          },
          {
            path: RoutePath.SystemInfoStandalone,
            lazy: () => import('./system-info/SystemInfoPage'),
          },
          ...(addDevRoutes
            ? [
                {
                  path: RoutePath.DevDebugStandalone,
                  lazy: () => import('./dev-debug/DevDebugPage'),
                },
                {
                  path: RoutePath.DevDataExportStandalone,
                  lazy: () => import('./dev-data-export/DevDataExportPage'),
                },
              ]
            : []),
        ],
      },
      {
        element: <ProtectedAdminRoutes />,
        children: [
          {
            path: RoutePath.ProtocolList,
            lazy: () => import('./admin-protocol-editor/ProtocolListPage'),
          },
          {
            path: RoutePath.Brands,
            lazy: () => import('./admin-brands/BrandsPage'),
          },
          {
            path: RoutePath.ProtocolEditWithoutId,
            lazy: () =>
              import('./admin-protocol-editor/AdminProtocolEditorPage'),
          },
          {
            path: RoutePath.ProtocolEdit(),
            lazy: () =>
              import('./admin-protocol-editor/AdminProtocolEditorPage'),
          },
          {
            path: RoutePath.NotificationTemplatesMatrix(),
            lazy: () =>
              import('./admin-protocol-editor/NotificationTemplatesMatrixPage'),
          },
          {
            path: RoutePath.NotificationTemplateEditor(),
            lazy: () =>
              import('./admin-protocol-editor/NotificationTemplateEditorPage'),
          },
        ],
      },
      {
        element: <ProtectedParticipationRoutes />,
        children: [
          {
            path: RoutePath.Home(),
            lazy: loadHomePage,
          },
          {
            path: RoutePath.ParticipationTask(),
            lazy: loadParticipationTaskPage,
          },
          {
            path: RoutePath.Schedule(),
            lazy: () => import('./schedule/SchedulePage'),
          },
          {
            path: RoutePath.StudyInfo(),
            lazy: () => import('./study-info/StudyInfoPage'),
          },
          {
            path: RoutePath.SystemInfoWithLayout(),
            lazy: () => import('./system-info/SystemInfoPage'),
          },
          ...(addDevRoutes
            ? [
                {
                  path: RoutePath.DevDebugWithLayout(),
                  lazy: () => import('./dev-debug/DevDebugPage'),
                },
                {
                  path: RoutePath.DevDataExportWithLayout(),
                  lazy: () => import('./dev-data-export/DevDataExportPage'),
                },
              ]
            : []),
        ],
      },
      {
        element: (
          <ProtectedParticipationRoutes withLayout={false} allowWhenNotActive />
        ),
        children: [
          {
            path: RoutePath.ParticipationNotFound,
            lazy: () => import('./not-found/NotFoundPage'),
          },
        ],
      },
      ...(addDevRoutes
        ? [
            {
              element: <DemoProtectedRoutes />,
              children: [
                {
                  path: RoutePath.DevDemo,
                  lazy: () => import('./dev-demo/DevDemoPage'),
                  children: [
                    {
                      index: true,
                      element: <Navigate to={DevDemoTabs.Slider} replace />,
                    },
                    {
                      path: DevDemoTabs.Slider,
                      lazy: () => import('./dev-demo/slider/DevDemoSliderPage'),
                    },
                    {
                      path: DevDemoTabs.Nrs,
                      lazy: () => import('./dev-demo/nrs/DevDemoNRSPage'),
                    },
                    {
                      path: DevDemoTabs.ErrorHandling,
                      lazy: () =>
                        import('./dev-demo/error-handling/DevDemoErrorHandlingPage'),
                    },
                    {
                      path: DevDemoTabs.EmbeddedApp,
                      lazy: () =>
                        import('./dev-demo/embedded-app/DevDemoEmbeddedAppPage'),
                    },
                    {
                      path: DevDemoTabs.CompliancePage,
                      lazy: () =>
                        import('./dev-demo/embedded-compliance/DevDemoCompliancePage'),
                    },
                    {
                      path: DevDemoTabs.DynamicTranslation,
                      lazy: () =>
                        import('./dev-demo/dynamic-translation/DevDemoDynamicTranslationPage'),
                    },
                    {
                      path: DevDemoTabs.CustomLayout,
                      lazy: () =>
                        import('./dev-demo/custom-layout/DevDemoCustomLayoutPage'),
                    },
                  ],
                },
              ],
            },
          ]
        : []),
      {
        path: RoutePath.SignedOut,
        lazy: () => import('./signed-out/SignedOutPage'),
      },
      {
        path: RoutePath.SomethingWentWrong,
        lazy: () => import('./something-went-wrong/SomethingWentWrongPage'),
      },
      {
        path: '*',
        lazy: () => import('./not-found/NotFoundPage'),
      },
    ],
  },
]);

// Clean up on module reload (HMR)
// https://vitejs.dev/guide/api-hmr
if (import.meta.hot) {
  import.meta.hot.dispose(() => router.dispose());
}
